import { ThemeColors, ThemeConfig } from '@tenants/types';
import { SiteLanguage } from 'types/locales';
import keywordsUS from '@tenants/definitions/shared/keywords-US';
import { EXP_COMMERCIAL_THEME_NAME } from '@tenants/types';
import { EXP_COMMERCIAL_ROOT_DOMAIN, getSubDomainUrl } from 'constants/domains';

//#region Public Constants
export const ExpCommercialUsThemeColors: ThemeColors = {
  primaryVeryLight: '#8ECFFF',
  primaryLight:'#A3B075',
  primary: '#0C0F24',
  primaryDark: '#0C0F24',
  primaryVeryDark: '#0C0F24',
  primaryBackground: '#FFF',
  primaryForSale: '#FFF',
  primarySold: '#6D071A',
  primarySoldFocus: '#6D071A',
  primaryPending: '#D94A3A',
  primaryPendingFocus: '#D94A3A',
  primaryFilters: '#0C0F24',
  primaryFontColor: '#0C0F24',
  primaryLinkColor: '#1B489B',
  primaryFocusLinkColor: '#1B489B',
  primaryInvertedBackground: '#FFFFFF',
  primaryBorderColor: '#5A5A5A',
  primaryFocusColor: '#F3F4F4',
  primaryFocusBorderColor: '#0C0F24',
  primaryChart: '#F3F3F4',
  secondaryBackground: '#FFF',
  secondaryBorderColor: '#5A5A5A',
  secondaryLinkColor: '#3D3F50',
  secondaryHoverColor: '#0C0F24',
  secondaryFocusColor: '#3D3F50',
  secondaryFocusBorderColor: '#5A5A5A',
  tertiaryBackground: '#EFEFEF',
  tertiaryBackgroundLight: '#3D3F50',
  tertiaryBorderColor: '#5A5A5A',
  tertiaryLinkColor: '#1B489B',
  quaternaryLinkColor: '#496DAF',
  quaternaryBorderColor: '#5A5A5A',
  quaternaryBackgroundLight: '#FFFFFF',
  transparentBackground: 'rgba(255, 255, 255, 0.1)',
  mapPinForSale: 'rgba(59, 80, 84, 1)',
  mapPinForSaleFocus: 'rgba(12, 15, 36, 1)',
  mapControlBackground: '#FFF',
  mapControlBackgroundHover: '#F3F4F4',
  mapControlBackgroundActive: '#E7E7E9',
  carouselArrowHover: '#F3F4F4',
  carouselArrowFocus: '#CECFD3',
  notFound404PinHeadColor: '#1B489B',
  notFound404PinBodyColor: '#496DAF',
};
//#endregion

const BASE_URL = getSubDomainUrl(EXP_COMMERCIAL_ROOT_DOMAIN, 'www');
//#region Theme Config
const ExpCommercialUsTheme: ThemeConfig<typeof EXP_COMMERCIAL_THEME_NAME> = {
  id: EXP_COMMERCIAL_THEME_NAME,
  name: 'eXp Commercial Brokerage',
  title: 'eXp Commercial Brokerage',
  faviconPath: '/favicon-exp-commercial.ico',
  metaDescription: 'eXp Commercial are pioneers of the virtual commercial &amp; residential real estate industry, using a cloud-based model for agents globally.',
  largeDesktopHeaderImage: '/next/assets/images/header-image-large-desktop-exp-ca.jpg',
  desktopHeaderImage: '/next/assets/images/header-image-desktop-exp-ca.jpg',
  mobileHeaderImage: '/next/assets/images/header-image-mobile-exp-ca.jpg',
  smallMobileImage: '/next/assets/images/header-image-small-mobile-exp-ca.jpg',
  ogSiteName: 'eXp Commercial Brokerage',
  ogImage: `${BASE_URL}/next/assets/images/exp-commercial/og.png`,
  ogImageAlt: 'A low-angle, close-up shot of a modern glass skyscraper with a sweeping, curved facade against a light blue sky.',
  schemaUrl: BASE_URL,
  schemaLogo: `${BASE_URL}/next/assets/images/exp-commercial/og.png`,
  schemaLocation: {
    addressCountry: 'United States',
    addressLocality: '',
    addressRegion: '',
    postalCode: '',
    streetAddress: '',
  },
  schemaFoundingDate: '2002',
  schemaFounders: [
    {
      '@type': 'Person',
      name: 'Sanford Glenn Darrel',
    },
    {
      '@type': 'Person',
      name: 'Brian Culhane',
    },
  ],
  colors: ExpCommercialUsThemeColors,
  fonts: {
    heading: 'Manrope, sans-serif',
    text: 'Manrope, sans-serif',
    headingWeight: '300',
    headingBoldWeight: '700',
  },
  decorations: {
    brushstrokes: false,
    boxShadow: '0px 2px 4px 0px #00000014',
    boxShadowLight: '0 0 8px 0 rgba(0, 0, 0, .1)',
    areaPageLineWidth: '51px',
  },
  buttons: {
    borderRadius: '25px',
    borderRadiusSmall: '8px',
    borderRadiusMedium: '8px',
    borderRadiusLarge: '100px',
    borderRadiusModal: '12px',
    borderRadiusBanner: '12px',
  },
  icons: {
    borderRadius: '100px',
  },
  style_overrides: {
    breadcrumbs: {
      mobileMarginTop: '1.5em',
      boxShadow: '0px 2px 4px 0px #00000014',
    },
    areaListingsPage: {
      pageTitleFontWeight: 'bold',
    },
    locationUnavailable: {
      imagePath: '/next/assets/images/listing-page/location-unavailable-exp.svg',
    },
    icon: {
      checkmarkCircleIcon: {
        fill: ExpCommercialUsThemeColors.primaryLinkColor,
      },
    },
  },
  menuLinks: [
    {
      id: 'join',
      label: 'Join',
      link: 'https://www.expcommercial.com/become-an-advisor',
    },
    {
      id: 'services',
      label: 'Services',
      link: 'https://www.expcommercial.com/services',
    },
    {
      id: 'insights',
      label: 'Insights',
      link: 'https://www.expcommercial.com/experts',
    },
    {
      id: 'people',
      label: 'People',
      link: getSubDomainUrl(EXP_COMMERCIAL_ROOT_DOMAIN, 'www'),
    },
    {
      id: 'about-us',
      label: 'About Us',
      link: 'https://www.expcommercial.com/about',
    },
  ],
  searchOptions: [
  ],
  contentBlockBuy: {
    title: 'Buying your next home',
    copy: 'eXp Realty transforms home buying into a seamless experience, tailored to your needs with cutting-edge technology and expert support. Discover a better way to buy a home with eXp.',
    image: '/next/assets/images/content-block/exp-buy.jpg',
    link: '/buy',
  },
  contentBlockSell: {
    title: 'Selling your home',
    copy: 'Discover a smarter selling experience with eXp Realty with a simplified process that blends advanced technology and market insight to sell your home effectively and efficiently.',
    image: '/next/assets/images/content-block/exp-sell.jpg',
    link: '/sell',
  },
  privacyPolicy: (language?: SiteLanguage) => {
    switch (language) {
      case SiteLanguage.French:
      case SiteLanguage.FrenchCA:
        return 'https://expworldholdings.com/privacy-policy-french/';
      default:
        return 'https://expworldholdings.com/privacy-policy/';
    }
  },
  termsOfUse: (language?: SiteLanguage) => {
    switch (language) {
      case SiteLanguage.French:
      case SiteLanguage.FrenchCA:
        return 'https://expworldholdings.com/terms-of-service-french/';
      default:
        return 'https://expworldholdings.com/terms-of-service/';
    }
  },
  keywords: keywordsUS,
};
//#endregion
export default ExpCommercialUsTheme;
